import { getFileUrl } from '@/lib/utils/file-url'
import { usePermission } from '@/lib/hooks/use-permission'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import { BsGrid } from 'react-icons/bs'
import {
    MdAdminPanelSettings,
    MdAutoAwesome,
    MdBusiness,
    MdCampaign,
    MdCorporateFare,
    MdDashboard,
    MdDescription,
    MdExpandLess,
    MdExpandMore,
    MdHealthAndSafety,
    MdLayers,
    MdLibraryBooks,
    MdMerge,
    MdBlock,
    MdNotificationsActive,
    MdShield,
    MdPerson,
    MdPeopleAlt,
    MdHub,
    MdMessage,
    MdSettings,
    MdWallet,
    MdWidgets,
} from 'react-icons/md'
import { Link, useLocation } from 'react-router-dom'

interface MenuItem {
    label: string
    icon: any
    href: string
    badge?: string | null
    segments?: string[]
    permission?: { module: string; resource: string }
}

interface MenuModule {
    label: string
    labelComponent?: React.ReactNode
    icon: any
    items: MenuItem[]
    segments?: string[]
    permissionModule?: string
}

const singleMenuItems: MenuItem[] = [
    {
        label: 'Dashboard',
        icon: MdDashboard,
        href: '/root/dashboard',
        badge: null,
        permission: { module: 'dashboard', resource: 'view' },
    },
]

const menuModulesFn = (segment: string, companyName: string): MenuModule[] => [
    {
        label: 'Organizações',
        icon: MdBusiness,
        permissionModule: 'person',
        items: [
            {
                label: 'Organizações',
                icon: MdCorporateFare,
                href: '/organizations',
                permission: { module: 'person', resource: 'clients' },
            },
        ],
    },
    {
        label: 'Financeiro',
        icon: MdWallet,
        permissionModule: 'commerce',
        items: [
            {
                label: 'Conta Asaas da Plataforma',
                icon: MdWallet,
                href: '/platform-account',
                permission: { module: 'person', resource: 'clients' },
            },
            {
                label: 'Tiers de Plano',
                icon: MdLayers,
                href: '/plan-tiers',
                permission: { module: 'person', resource: 'clients' },
            },
            {
                label: 'Planos de Assinaturas',
                icon: BsGrid,
                href: '/plans',
                permission: { module: 'person', resource: 'clients' },
            },
            {
                label: 'Configurações Financeiras',
                icon: MdSettings,
                href: '/settings/financial',
                permission: { module: 'person', resource: 'clients' },
            },
            {
                label: 'Política de Cobrança',
                icon: MdNotificationsActive,
                href: '/settings/dunning',
                permission: { module: 'person', resource: 'clients' },
            },
            {
                label: 'Política de Armazenamento',
                icon: MdShield,
                href: '/settings/storage',
                permission: { module: 'person', resource: 'clients' },
            },
        ],
    },
    {
        label: 'Cadastros gerais',
        icon: MdLibraryBooks,
        permissionModule: 'person',
        items: [
            {
                label: 'Códigos TUSS',
                icon: MdHealthAndSafety,
                href: '/bookings/tuss',
                permission: { module: 'person', resource: 'clients' },
            },
            {
                label: 'Unificação TUSS',
                icon: MdMerge,
                href: '/bookings/tuss/unify',
                permission: { module: 'person', resource: 'clients' },
            },
        ],
    },
    {
        // Não é só segurança: usuários, integrações, templates do WhatsApp e
        // módulos do sistema são configuração da plataforma inteira.
        label: 'Plataforma',
        icon: MdSettings,
        permissionModule: 'security',
        items: [
            {
                label: 'Usuários',
                icon: MdPeopleAlt,
                href: '/security/users',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Bloqueio de IP',
                icon: MdBlock,
                href: '/security/blocked-ips',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Consumo de IA',
                icon: MdAutoAwesome,
                href: '/platform/ai-usage',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Integrações',
                icon: MdHub,
                href: '/atendimento/integrations',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Templates do WhatsApp',
                icon: MdMessage,
                href: '/messaging/system-templates',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Avisos do Sistema',
                icon: MdCampaign,
                href: '/system-announcements',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Módulos do Sistema',
                icon: MdWidgets,
                href: '/system-modules',
                permission: { module: 'security', resource: 'permissions' },
            },
            {
                label: 'Permissões',
                icon: MdAdminPanelSettings,
                href: '/security/permissions',
                permission: { module: 'security', resource: 'permissions' },
            },
        ],
    },
]

const accountMenuItem: MenuItem = {
    label: 'Minha Conta',
    icon: MdPerson,
    href: '/account',
}

const policiesMenuItem: MenuItem = {
    label: 'Políticas e Termos',
    icon: MdDescription,
    href: '/policies',
}
/*
const relatoriosMenuItem: MenuItem = {
    label: 'Relatórios',
    icon: MdAssignment,
    href: '/master/relatorios',
}*/

interface MasterSidebarProps {
    onItemClick?: () => void
}

export const MasterSidebar = ({ onItemClick }: MasterSidebarProps) => {
    const location = useLocation()
    const { user, isMaster, isCompany } = useAuth()
    const { canAccessModule, canRead } = usePermission()
    const segment = 'OTHER'

    const menuModules = menuModulesFn(segment, user?.company?.name || '')

    const isModuleActive = (items: MenuItem[]) => {
        return items.some(item => {
            const exactMatch = location.pathname === item.href
            const prefixMatch =
                item.href !== '/' &&
                item.href !== '/admin' &&
                location.pathname.startsWith(item.href + '/')
            return exactMatch || prefixMatch
        })
    }

    // Verifica se um item é visível considerando segmento e permissões da role
    const isItemVisible = (item: MenuItem): boolean => {
        if (item.segments && !item.segments.includes(segment)) return false
        if (item.permission) {
            return canRead(item.permission.module, item.permission.resource)
        }
        return true
    }

    // Verifica se um módulo é visível
    const isModuleVisible = (module: MenuModule): boolean => {
        if (module.segments && !module.segments.includes(segment)) return false
        if (module.permissionModule) {
            return canAccessModule(module.permissionModule)
        }
        return true
    }

    // Calcula módulos que devem estar abertos baseado na rota atual
    const initialOpenModules = useMemo(() => {
        return menuModules
            .filter(module => isModuleActive(module.items))
            .map(module => module.label)
    }, [location.pathname])

    const [openModules, setOpenModules] = useState<string[]>(initialOpenModules)

    // Atualiza quando a rota mudar para abrir o módulo correto
    useEffect(() => {
        const activeModules = menuModules
            .filter(module => isModuleActive(module.items))
            .map(module => module.label)

        // Adiciona módulos ativos que não estão abertos
        setOpenModules(prev => {
            const newModules = [...prev]
            activeModules.forEach(m => {
                if (!newModules.includes(m)) {
                    newModules.push(m)
                }
            })
            return newModules
        })
    }, [location.pathname])

    const toggleModule = (moduleLabel: string) => {
        setOpenModules(prev =>
            prev.includes(moduleLabel)
                ? prev.filter(m => m !== moduleLabel)
                : [...prev, moduleLabel]
        )
    }

    const renderMenuItem = (item: MenuItem, parentItems?: MenuItem[]) => {
        const Icon = item.icon

        // Verifica se a rota atual corresponde exatamente ao href
        const isExactMatch = location.pathname === item.href

        // Verifica se a rota atual começa com o href seguido de / (para subrotas)
        const isPrefixMatch =
            item.href !== '/admin' &&
            item.href !== '/' &&
            location.pathname.startsWith(item.href + '/')

        // Se há itens irmãos (mesmo módulo), verifica se algum deles tem uma correspondência mais específica
        // Só considera irmãos que são MAIS específicos (href mais longo que o item atual)
        let hasMoreSpecificMatch = false
        if (parentItems) {
            hasMoreSpecificMatch = parentItems.some(sibling => {
                if (sibling.href === item.href) return false
                // Só considera irmãos com href mais longo (mais específicos)
                if (sibling.href.length <= item.href.length) return false
                const siblingExactMatch = location.pathname === sibling.href
                const siblingPrefixMatch =
                    sibling.href !== '/admin' &&
                    sibling.href !== '/' &&
                    location.pathname.startsWith(sibling.href + '/')
                return siblingExactMatch || siblingPrefixMatch
            })
        }

        // Item está ativo se:
        // 1. É correspondência exata, OU
        // 2. É correspondência por prefixo E não há correspondência mais específica em itens irmãos
        const isActive =
            isExactMatch || (isPrefixMatch && !hasMoreSpecificMatch)

        return (
            <Link
                key={item.href}
                to={item.href}
                onClick={onItemClick}
                className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-200 group relative
                    ${
                        isActive
                            ? 'bg-secondary text-primary-content shadow-md'
                            : 'text-base-content hover:bg-base-200/70'
                    }
                `}
            >
                {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-content rounded-r-full" />
                )}
                <Icon
                    className={`text-lg flex-shrink-0 ${
                        isActive
                            ? 'text-primary-content'
                            : 'text-base-content/70 group-hover:text-base-content'
                    }`}
                />
                <span
                    className={`font-medium text-sm flex-1 ${
                        isActive
                            ? 'text-primary-content'
                            : 'text-base-content group-hover:text-base-content'
                    }`}
                >
                    {item.label}
                </span>
                {item.badge && (
                    <span className="badge badge-xs badge-secondary">
                        {item.badge}
                    </span>
                )}
            </Link>
        )
    }

    return (
        <aside className="w-64 h-screen bg-white! from-base-100 to-base-200/50 border-r border-base-300/50 flex flex-col shadow-sm overflow-hidden">
            <div className="p-6 pb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/80 flex items-center justify-center shadow-lg overflow-hidden">
                        <img
                            src={
                                user?.company?.logoUrl
                                    ? getFileUrl(user.company.logoUrl)
                                    : '/logo192.png'
                            }
                            alt="Logo"
                            className="w-10 h-10 object-contain"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-lg font-bold text-base-content truncate">
                            {user?.company?.tradingName ||
                                user?.company?.name ||
                                'MediarDex'}
                        </h1>
                        <p className="text-xs text-base-content/60">
                            Painel de Controle
                        </p>
                    </div>
                </div>
            </div>

            <nav className="flex-1 px-3 pb-4 overflow-y-auto">
                <div className="space-y-1">
                    {singleMenuItems
                        .filter(isItemVisible)
                        .map(item => renderMenuItem(item))}

                    {menuModules.filter(isModuleVisible).map(module => {
                        const ModuleIcon = module.icon
                        const isOpen = openModules.includes(module.label)
                        const hasActiveItem = isModuleActive(module.items)

                        const filteredItems = module.items.filter(isItemVisible)

                        if (filteredItems.length === 0) return null

                        return (
                            <div key={module.label} className="space-y-1">
                                <button
                                    onClick={() => toggleModule(module.label)}
                                    className={`
                                            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                                            transition-all duration-200 group
                                            ${
                                                hasActiveItem
                                                    ? 'bg-secondary/10 text-secondary'
                                                    : 'text-base-content hover:bg-base-200/70'
                                            }
                                        `}
                                >
                                    <ModuleIcon
                                        className={`text-lg flex-shrink-0 ${
                                            hasActiveItem ||
                                            module.labelComponent !== undefined
                                                ? 'text-secondary'
                                                : 'text-base-content/70 group-hover:text-base-content'
                                        }`}
                                    />
                                    <span
                                        className={`font-semibold text-sm flex-1 text-left ${
                                            hasActiveItem
                                                ? 'text-secondary'
                                                : 'text-base-content group-hover:text-base-content'
                                        }`}
                                    >
                                        {module.labelComponent || module.label}
                                    </span>
                                    {isOpen ? (
                                        <MdExpandLess className="text-lg text-base-content/50" />
                                    ) : (
                                        <MdExpandMore className="text-lg text-base-content/50" />
                                    )}
                                </button>

                                {isOpen && (
                                    <div className="ml-3 pl-3 border-l-2 border-base-300/50 space-y-1">
                                        {filteredItems.map(item =>
                                            renderMenuItem(item, filteredItems)
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    <div className="pt-2 mt-2 border-t border-base-300/50 space-y-1">
                        {renderMenuItem(policiesMenuItem)}
                        {renderMenuItem(accountMenuItem)}
                    </div>
                </div>
            </nav>

            <div className="p-4 border-t border-base-300/50">
                <div className="bg-gradient-to-br from-secondary/10 to-secondary/10 rounded-lg p-3 border border-secondary/20">
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                            <MdAdminPanelSettings className="text-secondary text-lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-base-content mb-1">
                                Acesso Admin
                            </p>
                            <p className="text-xs text-base-content/60 leading-relaxed">
                                Gerencie todo o sistema
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </aside>
    )
}
